import React from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { useCurrency } from '@/hooks/useCurrency'
import { formatDayKey } from '@/lib/accountTimezone'

const chartConfig = {
  won_value: {
    label: 'Vendas',
    color: 'var(--chart-2)',
  },
} satisfies ChartConfig

interface DailySalesChartProps {
  data: { day: string; won_count: number; won_value: number }[]
  loading?: boolean
}

const DailySalesChart: React.FC<DailySalesChartProps> = ({ data, loading = false }) => {
  const { t } = useTranslation('reports')
  const { formatCurrency, formatCurrencyCompact } = useCurrency()
  // `item.day` is an account-timezone calendar day, already resolved — `formatDayKey`
  // renders it in the account locale without pushing it through a timezone again.
  const chartData = data.map((item) => ({
    ...item,
    label: formatDayKey(item.day, { day: '2-digit', month: '2-digit' }),
    fullLabel: formatDayKey(item.day, { dateStyle: 'medium' }),
  }))

  // With a full month on screen every tick would collide, so thin them out
  const tickInterval = chartData.length > 20 ? 3 : chartData.length > 12 ? 1 : 0
  const hasSales = chartData.some((item) => item.won_value > 0 || item.won_count > 0)

  if (loading) {
    return (
      <div className="rounded-[20px] border border-border bg-card p-5 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-4 h-5 w-1/3 rounded bg-card-muted" />
          <div className="h-[260px] w-full rounded-xl bg-card-muted" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 rounded-[20px] border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-col gap-1">
        <h3 className="text-[15px] font-semibold text-foreground">{t('dashboard.dailySalesTitle')}</h3>
        <p className="text-[12px] text-muted-foreground">
          {t('dashboard.dailySalesDesc')}
        </p>
      </div>

      {!hasSales ? (
        <div className="flex flex-1 items-center justify-center py-12">
          <p className="max-w-[260px] text-center text-[13px] text-muted-foreground">
            {t('dashboard.noSalesInPeriod')}
          </p>
        </div>
      ) : (
        <ChartContainer className="h-[260px] w-full" config={chartConfig}>
          <BarChart data={chartData} margin={{ left: 4, right: 4, top: 8, bottom: 0 }}>
            <CartesianGrid
              vertical={false}
              stroke="var(--chart-grid)"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              interval={tickInterval}
              tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={64}
              tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
              tickFormatter={(value: number) => formatCurrencyCompact(value)}
            />
            <ChartTooltip
              cursor={{ fill: 'var(--chart-cursor)', fillOpacity: 0.08 }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) =>
                    (payload?.[0]?.payload as { fullLabel?: string })?.fullLabel ?? ''
                  }
                  formatter={(value, _name, item) => {
                    const count = (item?.payload as { won_count?: number })?.won_count ?? 0
                    return (
                      <span className="flex flex-col">
                        <span className="font-semibold text-foreground">
                          {formatCurrency(Number(value))}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {count} {count === 1 ? 'venda' : 'vendas'}
                        </span>
                      </span>
                    )
                  }}
                />
              }
            />
            <Bar
              dataKey="won_value"
              fill="var(--color-won_value)"
              radius={[5, 5, 0, 0]}
              maxBarSize={44}
            />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  )
}

export default DailySalesChart
